/**
 * Public shared evaluation view — read-only, no auth required.
 *
 * Shows scores, feedback, company alignment, and follow-up questions.
 * Intentionally excludes: answer text, user info, coach notes.
 *
 * Accessed via /shared/:token — the token is a UUID that maps to an
 * evaluation's share_token column.
 */

import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";

import { getSharedEvaluation } from "../api/client";
import ScoreBar from "../components/ScoreBar";
import SimpleMarkdown from "../components/SimpleMarkdown";
import PageLoader from "../components/PageLoader";

export default function SharedEvaluation() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation("pages-account");

  const {
    data: evaluation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["shared-evaluation", token],
    queryFn: () => getSharedEvaluation(token!),
    enabled: !!token,
  });

  if (isLoading) {
    return <PageLoader message={t("SharedEvaluation.loadingMessage")} />;
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", py: 4 }}>
        <Alert severity="error">
          {t("SharedEvaluation.invalidLinkMessage")}
        </Alert>
      </Box>
    );
  }

  if (!evaluation) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", py: 4 }}>
        <Alert severity="warning">{t("SharedEvaluation.notFoundMessage")}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShareIcon color="primary" />
          <Typography variant="overline" color="primary">
            {t("SharedEvaluation.eyebrow")}
          </Typography>
        </Stack>
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: "1.4rem", sm: "2.125rem" } }}
        >
          {t("SharedEvaluation.heading")}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {evaluation.company_name && (
            <Chip label={evaluation.company_name} size="small" />
          )}
          {evaluation.target_role && (
            <Chip
              label={evaluation.target_role}
              size="small"
              variant="outlined"
            />
          )}
          <Chip
            label={new Date(evaluation.created_at).toLocaleDateString()}
            size="small"
            variant="outlined"
          />
        </Stack>
        {evaluation.question_text && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            &ldquo;{evaluation.question_text}&rdquo;
          </Typography>
        )}
      </Stack>

      <Grid container spacing={3}>
        {/* Score Panel */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 80 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("SharedEvaluation.dimensionScoresHeading")}
              </Typography>

              {evaluation.average_score !== null && (
                <Paper
                  elevation={0}
                  sx={{
                    textAlign: "center",
                    py: 2,
                    mb: 2,
                    bgcolor:
                      evaluation.average_score >= 4
                        ? "secondary.light"
                        : evaluation.average_score >= 3
                        ? "warning.light"
                        : "error.light",
                    borderRadius: 2,
                    // Fixed dark ink, not white: the severity tints above are
                    // hardcoded light in both themes, so white text fails WCAG
                    // contrast on them (~1.3:1 on warning.light). Matches the fix
                    // already applied to the sibling EvaluationDetail score tile.
                    color: "text.primary",
                  }}
                >
                  <Typography variant="h3" fontWeight={500}>
                    {evaluation.average_score}
                  </Typography>
                  <Typography variant="caption">
                    {t("SharedEvaluation.averageScoreLabel")}
                  </Typography>
                </Paper>
              )}

              <ScoreBar
                label={t("SharedEvaluation.situationLabel")}
                score={evaluation.situation_score}
              />
              <ScoreBar label={t("SharedEvaluation.taskLabel")} score={evaluation.task_score} />
              <ScoreBar label={t("SharedEvaluation.actionLabel")} score={evaluation.action_score} />
              <ScoreBar label={t("SharedEvaluation.resultLabel")} score={evaluation.result_score} />
              <ScoreBar
                label={t("SharedEvaluation.engagementLabel")}
                score={evaluation.engagement_score}
              />
              <Divider sx={{ my: 1 }} />
              <ScoreBar label={t("SharedEvaluation.overallLabel")} score={evaluation.overall_score} />
            </CardContent>
          </Card>

          {/* Company Alignment */}
          {evaluation.company_alignment && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("SharedEvaluation.companyAlignmentHeading")}
                </Typography>
                {evaluation.company_alignment.aligned_principles?.length >
                  0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      color="secondary.main"
                      fontWeight={500}
                    >
                      {t("SharedEvaluation.alignedLabel")}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {evaluation.company_alignment.aligned_principles.map(
                        (p: string) => (
                          <Chip
                            key={p}
                            label={p}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )
                      )}
                    </Stack>
                  </Box>
                )}
                {evaluation.company_alignment.reinforce_areas?.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="warning.main"
                      fontWeight={500}
                    >
                      {t("SharedEvaluation.reinforceLabel")}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {evaluation.company_alignment.reinforce_areas.map(
                        (a: string) => (
                          <Chip
                            key={a}
                            label={a}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )
                      )}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Full Evaluation */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("SharedEvaluation.fullEvaluationHeading")}
              </Typography>
              {evaluation.evaluation_markdown ? (
                <SimpleMarkdown content={evaluation.evaluation_markdown} />
              ) : (
                <Typography color="text.secondary">
                  {t("SharedEvaluation.noContentMessage")}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Follow-up Questions */}
          {evaluation.follow_up_questions &&
            evaluation.follow_up_questions.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("SharedEvaluation.followUpQuestionsHeading")}
                  </Typography>
                  {evaluation.follow_up_questions.map(
                    (q: any, i: number) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {i + 1}. {q.question}
                        </Typography>
                        {q.why_asked && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            <em>{t("SharedEvaluation.whyLabel")}</em> {q.why_asked}
                          </Typography>
                        )}
                        {q.how_to_prepare && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            <em>{t("SharedEvaluation.prepareLabel")}</em> {q.how_to_prepare}
                          </Typography>
                        )}
                      </Box>
                    )
                  )}
                </CardContent>
              </Card>
            )}

          {/* CareerForge CTA footer */}
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              mt: 2,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/favicon_1.png`}
                alt={t("SharedEvaluation.logoAlt")}
                sx={{ width: 36, height: 36, objectFit: "contain" }}
              />
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {t("SharedEvaluation.poweredByLabel")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("SharedEvaluation.footerTagline")}
              </Typography>
              <Button
                size="small"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                href="/"
                sx={{ mt: 0.5 }}
              >
                {t("SharedEvaluation.tryItFreeButton")}
              </Button>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
